import { isLeaveRecordVisible } from '../utils/leaveVisibility';
import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  AdjustLeaveBalanceInput,
  AdminLeaveBalance,
  LeaveBalanceEmployeeResult,
  LeaveBalanceSearchResult,
  LeaveBalanceTypeOption,
} from '../types/leaveBalanceAdmin';
import { computeSignedLeaveAvailable } from '../utils/leaveBalance';
import { parseMonthlyLopSummary } from '../utils/lopSummary';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function number(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function optionalNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function text(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (value == null) return fallback;
  return String(value);
}

export function normalizeAdminLeaveBalance(row: ObjectValue): AdminLeaveBalance {
  return {
    isLossOfPay: Boolean(Number(row.isLossOfPay)),
    daysTaken: number(row.daysTaken),
    employeeLeaveBalanceId: optionalNumber(row.employeeLeaveBalanceId),
    employeeId: optionalNumber(row.employeeId),
    leaveTypeId: number(row.leaveTypeId),
    leaveTypeCode: text(row.leaveTypeCode),
    leaveTypeName: text(row.leaveTypeName, 'Leave'),
    leavePolicyId: optionalNumber(row.leavePolicyId),
    leaveYear: optionalNumber(row.leaveYear),
    openingBalance: number(row.openingBalance),
    creditedDays: number(row.creditedDays),
    debitedDays: number(row.debitedDays),
    adjustedDays: number(row.adjustedDays),
    pendingLeaveDays: number(row.pendingLeaveDays),
    availableBalance: computeSignedLeaveAvailable(row) ?? 0,
    closingBalance: number(row.closingBalance),
    annualEntitlement: optionalNumber(row.annualEntitlement),
    monthlyAccrual: optionalNumber(row.monthlyAccrual),
    isLocked: Boolean(Number(row.isLocked || 0)),
    requiresBalance: row.requiresBalance == null ? true : Boolean(Number(row.requiresBalance)),
    allowFullDay: row.allowFullDay == null ? null : Boolean(Number(row.allowFullDay)),
    allowHalfDay: row.allowHalfDay == null ? null : Boolean(Number(row.allowHalfDay)),
  };
}

function normalizeType(row: ObjectValue): LeaveBalanceTypeOption {
  return {
    leaveTypeId: number(row.leaveTypeId),
    leaveTypeCode: text(row.leaveTypeCode),
    leaveTypeName: text(row.leaveTypeName, 'Leave'),
    requiresBalance: Boolean(Number(row.requiresBalance)),
    displayOrder: number(row.displayOrder),
  };
}

export function validateLeaveBalanceAdjustment(input: {
  availableBalance: string;
  reason: string;
}): string | null {
  const days = Number(input.availableBalance);
  if (!Number.isFinite(days) || days < -9999.99 || days > 9999.99) {
    return 'Available balance must be between -9999.99 and 9999.99.';
  }
  if (Math.abs(Math.round(days * 100) / 100 - days) > 1e-9) {
    return 'Available balance supports up to 2 decimal places.';
  }
  if (!input.reason.trim()) {
    return 'A reason is required when updating leave balance.';
  }
  if (input.reason.trim().length > 1000) {
    return 'Reason must not exceed 1000 characters.';
  }
  return null;
}

export async function searchEmployeeLeaveBalances(options: {
  search?: string;
  leaveTypeId?: number | null;
  leaveYear: number;
  page?: number;
  pageSize?: number;
}): Promise<LeaveBalanceSearchResult> {
  const response = await apiClient.get<Envelope<LeaveBalanceSearchResult>>(
    API_ENDPOINTS.LEAVE_BALANCES.LIST,
    {
      params: {
        search: options.search?.trim() || undefined,
        leaveTypeId: options.leaveTypeId || undefined,
        leaveYear: options.leaveYear,
        page: options.page ?? 1,
        pageSize: options.pageSize ?? 20,
      },
    },
  );
  const data = response.data.data;
  return {
    leaveYear: number(data.leaveYear, options.leaveYear),
    leaveTypes: (data.leaveTypes || []).filter(isLeaveRecordVisible).map(item => normalizeType(item as ObjectValue)),
    items: (data.items || []).map(item => ({
      employeeId: number(item.employeeId),
      employeeNumber: text(item.employeeNumber),
      displayName: text(item.displayName, text(item.employeeNumber, 'Employee')),
      departmentName: item.departmentName ? text(item.departmentName) : null,
      designationName: item.designationName ? text(item.designationName) : null,
      balances: (item.balances || []).filter(isLeaveRecordVisible).map(balance =>
        normalizeAdminLeaveBalance(balance as ObjectValue),
      ),
    })),
    pagination: {
      page: number(data.pagination?.page, options.page ?? 1),
      pageSize: number(data.pagination?.pageSize, options.pageSize ?? 20),
      totalRecords: number(data.pagination?.totalRecords),
      totalPages: number(data.pagination?.totalPages),
    },
  };
}

export async function getEmployeeLeaveBalancesAdmin(
  employeeId: number,
  leaveYear: number,
): Promise<LeaveBalanceEmployeeResult> {
  const response = await apiClient.get<Envelope<LeaveBalanceEmployeeResult>>(
    API_ENDPOINTS.LEAVE_BALANCES.EMPLOYEE(employeeId),
    { params: { leaveYear } },
  );
  const data = response.data.data;
  return {
    leaveYear: number(data.leaveYear, leaveYear),
    employee: {
      ...data.employee,
      employeeId: number(data.employee?.employeeId, employeeId),
      employeeNumber: text(data.employee?.employeeNumber),
      displayName: text(
        data.employee?.displayName,
        text(data.employee?.employeeNumber, 'Employee'),
      ),
    },
    balances: (data.balances || []).filter(isLeaveRecordVisible).map(item =>
      normalizeAdminLeaveBalance(item as ObjectValue),
    ),
    lopSummary: parseMonthlyLopSummary(data.lopSummary),
  };
}

export async function updateEmployeeLeaveBalance(
  employeeId: number,
  input: AdjustLeaveBalanceInput,
): Promise<AdminLeaveBalance> {
  const response = await apiClient.put<Envelope<{ balance: AdminLeaveBalance }>>(
    API_ENDPOINTS.LEAVE_BALANCES.EMPLOYEE(employeeId),
    input,
  );
  return normalizeAdminLeaveBalance((response.data.data?.balance || {}) as ObjectValue);
}
